import { type RefObject, useEffect } from "react"

import { isInteractivePointerTarget } from "./isInteractivePointerTarget"

const DRAG_THRESHOLD_PX = 4

/**
 * Pointer-based drag-to-pan on a native overflow scroller.
 * A press only becomes a drag after moving past 4px so clicks on
 * inputs/links/buttons inside the canvas still work. Presses that start
 * on interactive targets never arm pan.
 */
export const useDragScroll = (
  ref: RefObject<HTMLElement | null>,
  dragScroll = false
) => {
  useEffect(() => {
    if (!ref.current || !dragScroll) return

    const element = ref.current
    let pressed = false
    let dragging = false
    let pointerId: number | null = null
    let startX = 0
    let startY = 0
    let startLeft = 0
    let startTop = 0
    let previousScrollBehavior = ""
    let previousUserSelect = ""

    const setDraggingCursor = (isDragging: boolean) => {
      element.style.cursor = isDragging ? "grabbing" : "grab"
    }

    const clearTextSelection = () => {
      window.getSelection()?.removeAllRanges()
    }

    const swallowNextClick = () => {
      const onClick = (event: MouseEvent) => {
        event.preventDefault()
        event.stopPropagation()
        element.removeEventListener("click", onClick, true)
      }
      element.addEventListener("click", onClick, true)
    }

    const endDrag = () => {
      if (!pressed && !dragging) return

      if (dragging) {
        element.style.scrollBehavior = previousScrollBehavior
        swallowNextClick()
      }

      element.style.userSelect = previousUserSelect
      pressed = false
      dragging = false
      pointerId = null
      setDraggingCursor(false)
    }

    const onSelectStart = (event: Event) => {
      if (pressed || dragging) {
        event.preventDefault()
      }
    }

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0) return
      if (isInteractivePointerTarget(event.target)) return

      pressed = true
      dragging = false
      pointerId = event.pointerId
      startX = event.clientX
      startY = event.clientY
      startLeft = element.scrollLeft
      startTop = element.scrollTop
      // Block native text highlight while a pan gesture may start (labels/card copy).
      previousUserSelect = element.style.userSelect
      element.style.userSelect = "none"
    }

    const onPointerMove = (event: PointerEvent) => {
      if (!pressed || pointerId !== event.pointerId) return

      const dx = event.clientX - startX
      const dy = event.clientY - startY

      if (!dragging) {
        if (Math.hypot(dx, dy) <= DRAG_THRESHOLD_PX) return

        dragging = true
        previousScrollBehavior = element.style.scrollBehavior
        element.style.scrollBehavior = "auto"
        setDraggingCursor(true)
        clearTextSelection()
        element.setPointerCapture(event.pointerId)
      }

      event.preventDefault()
      element.scrollLeft = startLeft - dx
      element.scrollTop = startTop - dy
    }

    const onPointerUp = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return
      endDrag()
    }

    const onPointerCancel = (event: PointerEvent) => {
      if (pointerId !== event.pointerId) return
      endDrag()
    }

    element.style.cursor = "grab"
    element.addEventListener("pointerdown", onPointerDown)
    element.addEventListener("pointermove", onPointerMove)
    element.addEventListener("pointerup", onPointerUp)
    element.addEventListener("pointercancel", onPointerCancel)
    element.addEventListener("selectstart", onSelectStart)

    return () => {
      element.style.cursor = ""
      element.style.scrollBehavior = ""
      element.style.userSelect = ""
      element.removeEventListener("pointerdown", onPointerDown)
      element.removeEventListener("pointermove", onPointerMove)
      element.removeEventListener("pointerup", onPointerUp)
      element.removeEventListener("pointercancel", onPointerCancel)
      element.removeEventListener("selectstart", onSelectStart)
    }
  }, [ref, dragScroll])
}
